import React from "react";
import { createPortal } from "react-dom";

/**
 * "File too large" modal.
 *
 * Shown by the Brand Kit drag-and-drop zones (fonts / brand images /
 * logos / icons) when the user tries to upload a file whose size
 * exceeds the 25MB cap enforced in `BrandKitDropZone`. Same card
 * shell as `UnsupportedFileFormatModal` / `BrowserWarningModal` so
 * the warning system reads as one design family — only the copy and
 * the noun in the button change.
 *
 * The only action is the centered "Upload New <Subject>" button which
 * just dismisses the modal. The user retries with a smaller file via
 * the same drag-and-drop / file picker.
 */

const FONT_STACK = '"Inter", "Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

interface FileTooLargeModalProps {
  open: boolean;
  onClose: () => void;
  /** Lowercase noun for the rejected asset, used both in the body
   *  copy ("Please upload a smaller <subject>.") and in the button
   *  label ("Upload New <Subject>"). Defaults to "file" which works
   *  for any caller that doesn't know the asset type. */
  subject?: string;
}

const titleCase = (s: string): string => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));

export const FileTooLargeModal: React.FC<FileTooLargeModalProps> = ({ open, onClose, subject = "file" }) => {
  if (!open) return null;

  const subjectLower = subject.toLowerCase();
  const subjectTitle = titleCase(subjectLower);

  const modal = (
    <div
      onClick={(e) => {
        // Backdrop click also dismisses — there's no destructive
        // action to protect against; same affordance as the button.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(0, 8, 30, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          width: 555,
          maxWidth: "100%",
          padding: "24px 24px 32px 24px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 24,
          borderRadius: 8,
          border: "1px solid #E2E2FC",
          background: "#FFF",
          boxShadow: "0 2px 16px 0 rgba(37, 59, 97, 0.10)",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFamily: FONT_STACK,
              fontSize: 24,
              fontWeight: 700,
              lineHeight: "normal",
            }}
          >
            File too large
          </h2>
          <p
            style={{
              margin: 0,
              color: "#222336",
              textAlign: "center",
              fontFamily: FONT_STACK,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            This file exceeds the maximum upload size of 25MB. Please upload a smaller {subjectLower}.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          data-cypress-id="file-too-large-modal-upload-new-button"
          style={{
            display: "flex",
            minHeight: 40,
            maxHeight: 40,
            padding: "0 24px",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            borderRadius: 8,
            border: "none",
            // Mirrors the BrowserWarningModal "Get Google Chrome" CTA
            // (same fill, height, radius and typography) so the
            // warning system stays visually consistent.
            background: "#EE4360",
            color: "#FFF",
            textAlign: "center",
            fontFamily: FONT_STACK,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "120%",
            cursor: "pointer",
            boxSizing: "border-box",
          }}
        >
          Upload New {subjectTitle}
        </button>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};

export default FileTooLargeModal;
